import { useState, useEffect, useMemo } from 'react';
import { getMinuteData, getTimePoints } from '../utils/sectorDataUtils';

export const useSectorMomentum = () => {
  const [liveSectorData, setLiveSectorData] = useState(getMinuteData());
  const [refreshing, setRefreshing] = useState(false);
  const timePoints = useMemo(() => getTimePoints(), [refreshing]);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(timePoints.length - 1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visibleSectors, setVisibleSectors] = useState<string[]>([]);
  const [showAllSectors, setShowAllSectors] = useState(true);

  // Initialize visible sectors with all sectors
  useEffect(() => {
    if (liveSectorData.length > 0 && visibleSectors.length === 0) {
      // Show all sectors by default.
      setVisibleSectors(liveSectorData.map(sector => sector.id));
    }
  }, [liveSectorData, visibleSectors]);

  useEffect(() => {
    let interval: number | undefined;
    if (isPlaying) {
      interval = window.setInterval(() => {
        setCurrentTimeIndex(prev => {
          if (prev >= timePoints.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, timePoints.length]);

  const processedData = useMemo(() => {
    return timePoints.slice(0, currentTimeIndex + 1).map((time, idx) => {
      const point: Record<string, number | string> = { time };
      liveSectorData.forEach(sector => {
        const percentChange = sector.data[idx]?.value ?? 0;
        point[sector.id] = +percentChange.toFixed(2); // Optional rounding
      });
      return point;
    });
  }, [liveSectorData, currentTimeIndex, timePoints]);

  // Modify sectorRanks to include enhanced colors based on performance ranking
  const sectorRanks = useMemo(() => {
    if (!processedData.length) return [];

    const latest = processedData[processedData.length - 1];

    const rankedSectors = [...liveSectorData]
      .map(sector => ({
        id: sector.id,
        name: sector.name,
        color: sector.color, // original, overridden below
        change: latest[sector.id],
      }))
      .sort((a, b) => b.change - a.change);

    if (rankedSectors.length !== 17) {
      console.warn("Expected 17 sectors, but got", rankedSectors.length);
      return rankedSectors;
    }

    // Adjusted color arrays for 17 sectors
    const topColors = ['#00FF66', '#22CC55', '#44AA44'];                       // Top 3
    const midTopColors = ['#3A86FF', '#4CC9F0', '#4361EE', '#7209B7', '#560BAD']; // Next 5
    const midBottomColors = ['#FFC300', '#FB8500', '#FFBE0B', '#FAA307', '#F48C06']; // Next 5
    const bottomColors = ['#D90429', '#E63946', '#FF2222', '#990000'];         // Bottom 4

    return rankedSectors.map((sector, index) => {
      if (index < 3) return { ...sector, color: topColors[index] };
      if (index < 8) return { ...sector, color: midTopColors[index - 3] };
      if (index < 13) return { ...sector, color: midBottomColors[index - 8] };
      return { ...sector, color: bottomColors[index - 13] };
    });
  }, [processedData, liveSectorData]);

  // Get top and bottom performers for tooltips
  const topAndBottomPerformers = useMemo(() => {
    if (!sectorRanks.length) return { top: [], bottom: [] };
    return {
      top: sectorRanks.slice(0, 2),
      bottom: sectorRanks.slice(-2)
    };
  }, [sectorRanks]);

  const yDomain = useMemo(() => {
    if (!processedData.length) return [-2, 2];
    const latest = processedData[processedData.length - 1];
    const values = Object.keys(latest).filter(k => k !== "time").map(k => latest[k]);
    const max = Math.max(...values);
    const min = Math.min(...values);
    if (max > 2 || min < -2) {
      const padding = 0.5;
      return [Math.floor(min - padding), Math.ceil(max + padding)];
    }
    return [-2, 2];
  }, [processedData]);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      const updatedTimePoints = getTimePoints(); // fresh calculation
      setLiveSectorData(getMinuteData());
      setRefreshing(false);
      setCurrentTimeIndex(updatedTimePoints.length - 1);
    }, 1000);
  };

  const togglePlayback = () => {
    if (currentTimeIndex === timePoints.length - 1) {
      setCurrentTimeIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  const toggleSectorVisibility = (sectorId: string) => {
    setVisibleSectors(prev => {
      if (prev.includes(sectorId)) {
        return prev.filter(id => id !== sectorId);
      } else {
        return [...prev, sectorId];
      }
    });
  };

  const toggleAllSectors = () => {
    if (visibleSectors.length === liveSectorData.length) {
      // If all sectors are visible, show only top 3 positive and top 3 negative
      const topPositive = sectorRanks.slice(0, 3).map(s => s.id);
      const bottomNegative = sectorRanks.slice(-3).map(s => s.id);
      setVisibleSectors([...topPositive, ...bottomNegative]);
      setShowAllSectors(false);
    } else {
      // If not all sectors are visible, show all
      setVisibleSectors(liveSectorData.map(s => s.id));
      setShowAllSectors(true);
    }
  };

  return {
    liveSectorData,
    timePoints,
    currentTimeIndex,
    setCurrentTimeIndex,
    isPlaying,
    refreshing,
    processedData,
    sectorRanks,
    topAndBottomPerformers,
    visibleSectors,
    showAllSectors,
    yDomain,
    handleRefresh,
    togglePlayback,
    toggleSectorVisibility,
    toggleAllSectors,
  };
};
